import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  ShoppingBag, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Minus, 
  ZoomIn, 
  CheckCircle,
  Star  // ← added for featured badge
} from 'lucide-react';
import PageHero from '../components/PageHero';
import { SHOP_PRODUCTS, WHATSAPP_NUMBER } from '../data/shopData';

const PRODUCTS_PER_PAGE = 9;

// ─── Animation variants ───
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
});

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const filterButtonVariants = {
  inactive: { scale: 1 },
  active: { scale: 1.05, transition: { type: 'spring', stiffness: 300, damping: 20 } },
  hover: { scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 15 } },
};

const pageButtonVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

const Shop = () => {
  const [filter, setFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [quantities, setQuantities] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const categories = ['All', ...new Set(SHOP_PRODUCTS.map(p => p.category))];

  const filteredProducts = useMemo(() => {
    if (filter === 'All') return SHOP_PRODUCTS;
    return SHOP_PRODUCTS.filter(p => p.category === filter);
  }, [filter]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const handleSizeChange = (productId, sizeIndex) => {
    setSelectedSizes(prev => ({
      ...prev,
      [productId]: sizeIndex,
    }));
  };

  const getSelectedSize = (product) => {
    const idx = selectedSizes[product.id];
    const sizeIndex = idx !== undefined ? idx : 0;
    return product.sizes[sizeIndex];
  };

  const getQuantity = (productId) => {
    return quantities[productId] || 1;
  };

  const updateQuantity = (productId, delta) => {
    setQuantities(prev => {
      const current = prev[productId] || 1;
      const newQty = Math.max(1, current + delta);
      return { ...prev, [productId]: newQty };
    });
  };

  const handleBuyNow = (product) => {
    const selectedSize = getSelectedSize(product);
    if (!selectedSize) {
      alert('Please select a size first.');
      return;
    }
    const qty = getQuantity(product.id);
    const total = selectedSize.price * qty;
    const message = `Hi, I want to order: ${product.name}\nSize: ${selectedSize.label}\nQuantity: ${qty}\nPrice: GHS ${selectedSize.price} × ${qty} = GHS ${total}`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <section className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <PageHero
          badge="Shop"
          title="Frame <span class='text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500'>Collections</span>"
          subtitle="Beautiful frames to display your memories – from classic wood to modern metal, and digital templates too."
        >
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span className="text-yellow-400 font-semibold">{filteredProducts.length}</span>
            <span>frames available</span>
          </div>
        </PageHero>

        {/* ─── Filter Buttons ─── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mb-14"
        >
          {categories.map((cat) => {
            const isActive = filter === cat;
            return (
              <motion.button
                key={cat}
                variants={fadeUp(0.05 * categories.indexOf(cat))}
                whileHover="hover"
                animate={isActive ? 'active' : 'inactive'}
                variants={filterButtonVariants}
                onClick={() => setFilter(cat)}
                className={`group relative px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  isActive
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/25'
                    : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/5'
                }`}
              >
                {cat}
                {isActive && (
                  <motion.span
                    layoutId="activeFilterIndicator"
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-yellow-400"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* ─── Products Grid ─── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage + filter}
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {currentProducts.map((product, index) => (
              <motion.div
                key={product.id}
                variants={fadeUp(0.08 * index)}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-yellow-500/40 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-500/10"
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* ─── Image with rounded edges ─── */}
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl m-4 cursor-pointer">
                  <motion.img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    initial={false}
                    animate={{
                      scale: hoveredId === product.id ? 1.08 : 1,
                    }}
                    transition={{ duration: 0.6 }}
                    loading="lazy"
                    onClick={() => openLightbox(product.image)}
                  />
                  {/* ─── Zoom hint on hover ─── */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                    <span className="px-4 py-2 rounded-full bg-yellow-500/80 text-white text-sm font-medium backdrop-blur-sm flex items-center gap-2">
                      <ZoomIn size={16} /> Click to enlarge
                    </span>
                  </div>
                  
                </div>

                {/* ─── Product details below image ─── */}
                <div className="px-4 pb-4">
                  {/* <h3 className="text-xl font-semibold text-white">{product.name}</h3> */}

                  {/* ─── Size selector ─── */}
                  <div className="mt-3">
                    <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">
                      Select Size
                    </label>
                    <select
                      value={selectedSizes[product.id] ?? 0}
                      onChange={(e) => handleSizeChange(product.id, parseInt(e.target.value))}
                      className="w-full bg-black/60 text-white border border-white/20 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-200"
                    >
                      {product.sizes.map((size, idx) => (
                        <option key={idx} value={idx}>
                          {size.label} – GHS {size.price}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ─── Price, stock, quantity ─── */}
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-yellow-400">
                          GHS {getSelectedSize(product).price}
                        </span>
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle size={12} /> In Stock
                        </span>
                      </div>
                    </div>

                    {/* ─── Quantity selector ─── */}
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 uppercase tracking-wider">Qty</span>
                      <div className="flex items-center gap-1 bg-black/40 rounded-lg border border-white/10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(product.id, -1);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 rounded-l-lg transition"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-white text-sm font-medium">
                          {getQuantity(product.id)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(product.id, 1);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 rounded-r-lg transition"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>

                    {/* ─── Buy Now button ─── */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyNow(product);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-sm font-medium hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
                    >
                      <ShoppingBag size={16} /> Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* ─── Empty State ─── */}
        {filteredProducts.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <p className="text-gray-400 text-lg">No frames found in this category.</p>
          </motion.div>
        )}

        {/* ─── Pagination ─── */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center items-center gap-2 mt-14"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={18} />
            </motion.button>
            <AnimatePresence mode="wait">
              {getPageNumbers().map((page) => (
                <motion.button
                  key={page}
                  variants={pageButtonVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => goToPage(page)}
                  className={`px-4 py-2 rounded-full text-sm transition ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/25'
                      : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/5'
                  }`}
                >
                  {page}
                </motion.button>
              ))}
            </AnimatePresence>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={18} />
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* ─── Lightbox ─── */}
      {lightboxOpen && lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-gray-400 hover:text-white text-sm flex items-center gap-2 transition"
            >
              Close ✕
            </button>
            <img
              src={lightboxImage}
              alt="Product preview"
              className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Shop;